import { useEffect, useRef, useState } from 'react';
import { apiService } from '../../../services/apiService';
import type { SwaggerOutline } from '../../../types/mappingDocument';

type OutlineState =
  | { status: 'idle' }
  | { status: 'loading'; fileId: string }
  | { status: 'ready'; fileId: string; outline: SwaggerOutline }
  | { status: 'error'; fileId: string; message: string };

/**
 * Tags, operations and spec info for one uploaded swagger file. Results are cached per file for the page's
 * lifetime so switching between files doesn't refetch, and a stale response never overwrites a newer selection.
 */
export function useSwaggerOutline(fileId: string | null) {
  const cache = useRef(new Map<string, SwaggerOutline>());
  const [state, setState] = useState<OutlineState>({ status: 'idle' });

  useEffect(() => {
    if (!fileId) {
      setState({ status: 'idle' });
      return;
    }
    const cached = cache.current.get(fileId);
    if (cached) {
      setState({ status: 'ready', fileId, outline: cached });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading', fileId });
    apiService
      .getSwaggerTagsAndOperations(fileId)
      .then((outline) => {
        cache.current.set(fileId, outline);
        if (!cancelled) setState({ status: 'ready', fileId, outline });
      })
      .catch((err: any) => {
        console.error('Failed to load swagger tags and operation IDs:', err);
        if (!cancelled) {
          setState({
            status: 'error',
            fileId,
            message: err?.response?.data?.message || 'Failed to load tags and operation IDs from swagger file',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fileId]);

  // Guard against one render where `state` still describes the previous file.
  const current = state.status !== 'idle' && state.fileId === fileId ? state : null;
  return {
    outline: current?.status === 'ready' ? current.outline : null,
    loading: current?.status === 'loading' || (!!fileId && !current),
    error: current?.status === 'error' ? current.message : null,
  };
}
